import { Link } from 'react-router-dom'
import { HeartIcon } from '@heroicons/react/24/outline'
import ImageWithFallback from '../ui/ImageWithFallback'

const footerNavigation = {
  main: [
    { name: 'About', href: '/about' },
    { name: 'Challenges', href: '/challenges' },
    { name: 'Leaderboard', href: '/leaderboard' },
    { name: 'FAQ', href: '/faq' },
  ],
  support: [
    { name: 'Terms & Conditions', href: '/terms' },
    { name: 'Privacy Policy', href: '/privacy' },
    { name: 'Contact Us', href: '/contact' },
  ],
  social: [
    {
      name: 'Twitter',
      href: '#',
      icon: (props) => (
        <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
          <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
        </svg>
      ),
    },
    {
      name: 'GitHub',
      href: '#',
      icon: (props) => (
        <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
          <path
            fillRule="evenodd"
            d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
    {
      name: 'LinkedIn',
      href: '#',
      icon: (props) => (
        <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
      ),
    },
  ],
}

export default function Footer() {
  return (
    <footer className="relative aurora-bg border-t border-gold-500/30">
      <div className="absolute inset-0 golden-mesh opacity-10"></div>
      <div className="relative max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:py-20 lg:px-8">
        
        {/* Main Footer Content */}
        <div className="xl:grid xl:grid-cols-3 xl:gap-12">
          
          {/* Brand Section */}
          <div className="space-y-8 xl:col-span-1">
            <div className="flex items-center group">
              <div className="mr-4">
                <ImageWithFallback
                  src="/images/zova_labs_right.png"
                  alt="ZovaLabs Logo"
                  className="w-12 h-12 object-contain group-hover:scale-110 transition-transform duration-300"
                />
              </div>
              <div>
                <span className="text-2xl font-display font-bold holographic-text animate-holographic-shift">
                  ZovaLabs
                </span>
                <div className="text-sm text-gray-400 font-medium">
                  AI Innovation Platform
                </div>
              </div>
            </div>
            
            <p className="text-gray-300 text-lg leading-relaxed">
              Leading AI innovation platform empowering researchers, developers, and organizations to build the future. 
              Join our global community of innovators and make a real impact!
            </p>
            
            {/* Social Links */}
            <div className="flex space-x-4">
              {footerNavigation.social.map((item, index) => (
                <a
                  key={item.name}
                  href={item.href}
                  className="group relative w-12 h-12 glass-morphism rounded-xl flex items-center justify-center hover:scale-110 transition-all duration-300"
                  style={{animationDelay: `${index * 0.1}s`}}
                >
                  <item.icon className="h-5 w-5 text-gray-400 group-hover:text-gold-400 transition-colors duration-200" />
                  <div className="absolute inset-0 rounded-xl bg-gradient-gold opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                  <span className="sr-only">{item.name}</span>
                </a>
              ))}
            </div>

            {/* Decorative Element */}
            <div className="flex items-center space-x-2 pt-4">
              <div className="w-8 h-0.5 bg-gradient-gold animate-gold-shimmer"></div>
              <div className="w-2 h-2 bg-gold-400 rounded-full animate-pulse"></div>
              <div className="w-12 h-0.5 bg-gradient-gold animate-gold-shimmer" style={{animationDelay: '0.5s'}}></div>
            </div>
          </div>
          {/* Navigation Links */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-12 xl:mt-0 xl:col-span-2">
            
            {/* Navigation Section */}
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-display font-semibold text-gold-400 mb-6 relative">
                  Navigation
                  <div className="absolute -bottom-2 left-0 w-12 h-0.5 bg-gradient-gold animate-gold-shimmer"></div>
                </h3>
                <ul className="space-y-4">
                  {footerNavigation.main.map((item, index) => (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        className="group flex items-center text-gray-300 hover:text-gold-400 transition-all duration-300"
                        style={{animationDelay: `${index * 0.1}s`}}
                      >
                        <div className="w-1 h-1 bg-gold-400 rounded-full mr-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        <span className="group-hover:translate-x-1 transition-transform duration-200">{item.name}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-display font-semibold text-gold-400 mb-6 relative">
                  Support
                  <div className="absolute -bottom-2 left-0 w-12 h-0.5 bg-gradient-gold animate-gold-shimmer" style={{animationDelay: '0.5s'}}></div>
                </h3>
                <ul className="space-y-4">
                  {footerNavigation.support.map((item, index) => (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        className="group flex items-center text-gray-300 hover:text-gold-400 transition-all duration-300"
                        style={{animationDelay: `${index * 0.1 + 0.3}s`}}
                      >
                        <div className="w-1 h-1 bg-gold-400 rounded-full mr-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        <span className="group-hover:translate-x-1 transition-transform duration-200">{item.name}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Contact Section */}
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-display font-semibold text-gold-400 mb-6 relative">
                  Contact
                  <div className="absolute -bottom-2 left-0 w-12 h-0.5 bg-gradient-gold animate-gold-shimmer" style={{animationDelay: '1s'}}></div>
                </h3>
                <div className="space-y-6">
                  <div className="glass-morphism p-4 rounded-xl">
                    <a 
                      href="mailto:hello@zovalabs.com" 
                      className="text-gray-300 hover:text-gold-400 transition-colors duration-300 flex items-center group"
                    >
                      <div className="w-2 h-2 bg-gold-400 rounded-full mr-3 animate-pulse"></div>
                      <span className="group-hover:holographic-text">info@zovalabs.co.ke</span>
                    </a>
                  </div>
                  <div className="glass-morphism p-4 rounded-xl">
                    <div className="text-gray-300 flex items-start">
                      <div className="w-2 h-2 bg-gold-400 rounded-full mr-3 mt-2 animate-pulse" style={{animationDelay: '0.5s'}}></div>
                      <div>
                        <div>Innovation District</div>
                        <div>San Francisco, CA</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Newsletter Signup */}
              <div className="luxury-card p-6 rounded-2xl">
                <h4 className="text-lg font-display font-semibold text-white mb-4">
                  Stay Updated
                </h4>
                <p className="text-gray-300 text-sm mb-4">
                  Get the latest updates on challenges and events
                </p>
                <div className="flex">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    className="flex-1 px-4 py-2 bg-dark-800 border border-gold-500/30 rounded-l-lg text-white placeholder-gray-400 focus:outline-none focus:border-gold-400 transition-colors duration-200"
                  />
                  <button className="px-6 py-2 bg-gradient-gold text-dark-900 rounded-r-lg font-medium hover:shadow-lg transition-all duration-200">
                    Subscribe
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="mt-16 pt-8 border-t border-gold-500/20 relative">
          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-0.5 bg-gradient-gold animate-gold-shimmer"></div>
          
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
            <div className="flex items-center space-x-2">
              <span className="text-gray-400">Made with</span>
              <HeartIcon className="w-4 h-4 text-red-500 animate-pulse" />
              <span className="text-gray-400">by</span>
              <span className="text-gold-400 font-medium">ZovaLabs Team</span>
            </div>
            
            <div className="flex items-center space-x-4">
              <p className="text-gray-400">
                &copy; 2024 <span className="holographic-text animate-holographic-shift">ZovaLabs</span>. All rights reserved.
              </p>
              <div className="flex items-center space-x-1">
                <div className="w-1 h-1 bg-gold-400 rounded-full animate-pulse"></div>
                <div className="w-1 h-1 bg-gold-500 rounded-full animate-pulse" style={{animationDelay: '0.5s'}}></div>
                <div className="w-1 h-1 bg-gold-600 rounded-full animate-pulse" style={{animationDelay: '1s'}}></div>
              </div>
            </div>
          </div>
        </div>

        {/* Floating Decorative Elements */}
        <div className="absolute top-8 right-8 w-3 h-3 bg-gold-400/30 rounded-full animate-float"></div>
        <div className="absolute bottom-8 left-8 w-2 h-2 bg-gold-500/40 rounded-full animate-pulse"></div>
      </div>
    </footer>
  )
}